import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "preact/jsx-runtime";
const DOT_WINDOW_SIZE = 5;
export function ProgressBar({ settings, styles, currentIndex, totalOffers, hasOfferwallInProgressRow = false, offerwallAvailable = false, isMobileLayout = false, onOfferSelect, onPrevious, onNext, }) {
    // Hide on mobile when publisher has opted out of the mobile progress bar
    if (isMobileLayout && settings.hide_progress_bar_mobile) {
        return null;
    }
    // Don't render if only one offer
    if (totalOffers <= 1) {
        return null;
    }
    // If fixed_progress_bar is false, don't render on first offer
    if (!settings.fixed_progress_bar && currentIndex === 0) {
        return null;
    }
    const containerStyles = {
        display: 'flex',
        justifyContent: hasOfferwallInProgressRow || offerwallAvailable ? 'start' : 'center',
        alignItems: 'center',
        gap: '8px',
    };
    const isDots = settings.progress_bar_type?.toLowerCase() === 'dots';
    const stepperBg = styles.header?.background || '#007bff';
    const stepperTextColor = styles.header?.textColor || '#ffffff';
    // When fixed_progress_bar is false, hide arrows and disable all navigation
    // When fixed_progress_bar is true, show arrows and allow full navigation
    const showArrows = settings.fixed_progress_bar === true;
    const allowNavigation = settings.fixed_progress_bar === true;
    const renderDots = () => {
        // Sliding window: when there are more than DOT_WINDOW_SIZE offers, show only
        // DOT_WINDOW_SIZE dots centred on the active offer (clamped at edges).
        const useWindow = totalOffers > DOT_WINDOW_SIZE;
        const windowStart = useWindow
            ? Math.max(0, Math.min(currentIndex - 2, totalOffers - DOT_WINDOW_SIZE))
            : 0;
        const visibleCount = useWindow ? DOT_WINDOW_SIZE : totalOffers;
        const lastWindowPos = visibleCount - 1;
        return (_jsxs(_Fragment, { children: [showArrows && onPrevious && totalOffers > 1 && (_jsx("span", { className: 'adpx_left-chevron', style: getArrowButtonStyles(), onClick: onPrevious, children: _jsx(LeftArrowIcon, { size: 20 }) })), _jsx("div", { style: { display: 'flex', gap: '6px', alignItems: 'center' }, children: Array.from({ length: visibleCount }, (_, posInWindow) => {
                        const index = windowStart + posInWindow;
                        const isActive = index === currentIndex;
                        // Edge dots signal hidden offers: only mark as edge when offers are actually hidden
                        // in that direction (i.e. windowStart > 0 for left, window hasn't reached the end for right).
                        const isEdge = useWindow && ((posInWindow === 0 && windowStart > 0) ||
                            (posInWindow === lastWindowPos && windowStart + DOT_WINDOW_SIZE < totalOffers));
                        return (_jsx("div", { className: 'adpx_stepper-wrapper adpx_dot-container', style: {
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '0 2px',
                            }, children: _jsx("div", { className: 'adpx_dot-item', style: getDotStyles(isActive, styles, !allowNavigation, isEdge), onClick: allowNavigation ? () => onOfferSelect?.(index) : undefined, "data-visual-index": index }) }, index));
                    }) }), showArrows && onNext && totalOffers > 1 && (_jsx("span", { className: 'adpx_right-chevron', style: getArrowButtonStyles(), onClick: onNext, children: _jsx(RightArrowIcon, { size: 20 }) }))] }));
    };
    const renderNumbers = () => (_jsxs(_Fragment, { children: [showArrows && onPrevious && totalOffers > 1 && (_jsx("span", { className: 'adpx_left-chevron', style: getArrowButtonStyles(), onClick: onPrevious, children: _jsx(LeftArrowIcon, { size: 30 }) })), _jsx("div", { style: { display: 'flex', gap: '8px', alignItems: 'center' }, children: Array.from({ length: totalOffers }, (_, index) => {
                    // When fixed_progress_bar is false, disable all navigation (visual only)
                    // When fixed_progress_bar is true, allow full navigation
                    const canNavigateTo = allowNavigation;
                    return (_jsx("div", { className: 'adpx_stepper-wrapper', children: _jsx("div", { className: 'stepper-item completed', children: _jsx("div", { className: 'step-counter', style: getNumberStepStyles(index <= currentIndex, stepperBg, stepperTextColor, !canNavigateTo), onClick: canNavigateTo ? () => onOfferSelect?.(index) : undefined, children: index + 1 }) }) }, index));
                }) }), showArrows && onNext && totalOffers > 1 && (_jsx("span", { className: 'adpx_right-chevron', style: getArrowButtonStyles(), onClick: onNext, children: _jsx(RightArrowIcon, { size: 30 }) }))] }));
    return (_jsx("div", { className: 'adpx_progressbar-wrapper', style: containerStyles, children: _jsx("div", { className: 'adpx_progressbar-container', children: _jsx("div", { className: 'adpx_stepper-container', style: { display: 'flex', alignItems: 'center' }, children: isDots ? renderDots() : renderNumbers() }) }) }));
}
function getDotStyles(isActive, styles, isDisabled, isEdge) {
    const size = isActive ? '10px' : isEdge ? '6px' : '8px';
    return {
        width: size,
        height: size,
        borderRadius: '50%',
        border: 'none',
        backgroundColor: isActive ? styles.header?.background : '#d3d3d3',
        cursor: isDisabled ? 'default' : 'pointer',
        transition: 'all 0.3s ease',
        transform: isActive ? 'scale(1.3)' : 'scale(1)',
        padding: 0,
        opacity: isDisabled ? 0.5 : 1,
    };
}
function getNumberStepStyles(isCompleted, stepperBg, stepperTextColor, isDisabled) {
    return {
        position: 'relative',
        zIndex: 5,
        width: '30px',
        height: '30px',
        borderRadius: '50%',
        border: 'none',
        backgroundColor: isCompleted ? stepperBg : 'rgb(211, 211, 211)',
        color: isCompleted ? stepperTextColor : '#666',
        cursor: isDisabled ? 'default' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontWeight: 'bold',
        transition: 'all 0.3s ease',
        opacity: isDisabled ? 0.5 : 1,
    };
}
function getArrowButtonStyles() {
    return {
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 1,
    };
}
// Arrow icons from OfferImage component
function LeftArrowIcon({ size }) {
    return (_jsxs("svg", { height: size, width: size, viewBox: '0 0 24 24', children: [_jsx("path", { d: 'M12,0A12,12,0,1,0,24,12,12,12,0,0,0,12,0Zm0,22A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' }), _jsx("polygon", { points: '14.11 5.67 7.77 12 14.11 18.33 15.52 16.92 10.6 12 15.52 7.08 14.11 5.67' })] }));
}
function RightArrowIcon({ size }) {
    return (_jsxs("svg", { height: size, width: size, viewBox: '0 0 24 24', children: [_jsx("path", { d: 'M12,0A12,12,0,1,0,24,12,12,12,0,0,0,12,0Zm0,22A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' }), _jsx("polygon", { points: '8.48 7.08 13.4 12 8.48 16.92 9.89 18.33 16.23 12 9.89 5.67 8.48 7.08' })] }));
}
